import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Home, PackageCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import EstadoBadge from '@/components/dispositivos/EstadoBadge';
import { useCatalogosDispositivos } from '@/components/dispositivos/catalogos';
import { fechaCorta, textoDonde, TIPO_LABEL, type ResumenStock, type TipoDispositivo } from '@/lib/dispositivos';

/**
 * El stock: una tarjeta por tipo contra su mínimo, con la cuarentena y la
 * reparación a la vista pero fuera de lo disponible; el kit de casa nueva
 * (cuántas casas completas se pueden montar y qué tipo se acaba primero); y
 * la lista de unidades sin instalar, para elegir cuál se lleva uno.
 */
export default function DispositivosStockPage() {
  const { catalogos } = useCatalogosDispositivos();
  const { data, loading, error } = useApi<ResumenStock>(() => api.getDispositivosStock(), []);
  const [tipoElegido, setTipoElegido] = useState<TipoDispositivo | null>(null);

  if (loading) return <LoadingSpinner />;
  if (error || !data) return <EmptyState title="No se ha podido cargar el stock" description={error ?? undefined} />;

  const unidades = tipoElegido ? data.unidades.filter((u) => u.tipo === tipoElegido) : data.unidades;
  const kit = data.kit;
  const kitTipos = data.porTipo.filter((f) => f.kit > 0);

  return (
    <div className="space-y-6">
      <p className="text-sm text-sereno-dark/50 -mt-1">
        {data.tiposBajoMinimo === 0 ? 'Todos los tipos llegan al mínimo.' : `${data.tiposBajoMinimo} tipo${data.tiposBajoMinimo === 1 ? '' : 's'} por debajo del mínimo.`}{' '}
        La cuarentena y la reparación no cuentan como disponibles. «Sin estrenar» no es un estado que nadie marque: es que
        su historial no tiene ninguna instalación. Los mínimos y el kit se cambian en Configuración › Dispositivos.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {data.porTipo.map((f) => {
          const activo = tipoElegido === f.tipo;
          return (
            <button
              key={f.tipo}
              type="button"
              onClick={() => setTipoElegido(activo ? null : f.tipo)}
              className={`card text-left transition-colors ${activo ? 'ring-2 ring-sereno-dark' : ''} ${f.bajoMinimo ? 'border border-red-200 bg-red-50/40' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-semibold text-sereno-dark/60 uppercase tracking-wider">{TIPO_LABEL[f.tipo]}</p>
                {f.bajoMinimo && <AlertTriangle size={14} className="text-red-600 flex-shrink-0" />}
              </div>
              <p className={`text-3xl font-bold tabular-nums mt-1 ${f.bajoMinimo ? 'text-red-700' : ''}`}>
                {f.disponibles}
                <span className="text-sm font-normal text-sereno-dark/40"> / mín. {f.minimo}</span>
              </p>
              <p className="text-xs text-sereno-dark/50 mt-1" title="Sin estrenar = nunca se ha instalado, según su historial">
                {f.sinEstrenar} sin estrenar · {f.usados} ya usado{f.usados === 1 ? '' : 's'}
              </p>
              {(f.cuarentena > 0 || f.reparacion > 0) && (
                <p className="text-xs text-amber-700 mt-0.5">
                  {f.cuarentena > 0 ? `${f.cuarentena} en cuarentena` : ''}
                  {f.cuarentena > 0 && f.reparacion > 0 ? ' · ' : ''}
                  {f.reparacion > 0 ? `${f.reparacion} en reparación` : ''}
                </p>
              )}
              <p className="text-[11px] text-sereno-dark/35 mt-1">{f.instalados} instalado{f.instalados === 1 ? '' : 's'}</p>
            </button>
          );
        })}
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Home size={16} className="text-sereno-dark/50" />
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Kit de casa nueva</h3>
        </div>
        {kitTipos.length === 0 ? (
          <p className="text-sm text-sereno-dark/40">Sin kit configurado. Ponlo en Configuración › Dispositivos.</p>
        ) : (
          <>
            <p className="text-sm">
              Con lo disponible se pueden montar{' '}
              <span className={`font-bold ${kit.casasCompletas === 0 ? 'text-red-700' : ''}`}>{kit.casasCompletas}</span>{' '}
              casa{kit.casasCompletas === 1 ? '' : 's'} completa{kit.casasCompletas === 1 ? '' : 's'}
              {kit.cuelloDeBotella && <> · lo que se acaba primero: <span className="font-medium">{TIPO_LABEL[kit.cuelloDeBotella]}</span></>}.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              {kitTipos.map((f) => (
                <span key={f.tipo} className={`text-xs px-2 py-1 rounded-full ${f.disponibles >= f.kit ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {f.kit} × {TIPO_LABEL[f.tipo]} <span className="opacity-60">({f.disponibles} disp.)</span>
                </span>
              ))}
            </div>
            {kit.faltaParaUna.length > 0 && (
              <p className="text-xs text-red-700 mt-3">
                Para la siguiente casa falta: {kit.faltaParaUna.map((f) => `${f.necesarios - f.disponibles} × ${TIPO_LABEL[f.tipo]}`).join(', ')}.
              </p>
            )}
          </>
        )}
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider flex items-center gap-2">
            <PackageCheck size={15} /> Unidades sin instalar
            {tipoElegido && <span className="normal-case tracking-normal font-normal text-sereno-dark/40">· {TIPO_LABEL[tipoElegido]}</span>}
          </h3>
          <span className="text-xs text-sereno-dark/40">{unidades.length}</span>
        </div>
        {unidades.length === 0 ? (
          <p className="text-sm text-sereno-dark/40 px-6 py-6">Nada en stock{tipoElegido ? ' de este tipo' : ''}.</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <Th>Dispositivo</Th>
                <Th>Estado</Th>
                <Th>Dónde</Th>
                <Th>Desde</Th>
                <Th>Incidencias</Th>
              </tr>
            </thead>
            <tbody>
              {unidades.map((u) => (
                <tr key={u.id} className="border-b border-gray-50 hover:bg-sereno-blue/20 transition-colors">
                  <td className="px-6 py-3">
                    <Link to={`/dispositivos/${u.id}`} className="font-medium text-sm font-mono hover:underline">{u.idFabricante}</Link>
                    <p className="text-xs text-sereno-dark/50 truncate max-w-[240px]">
                      {TIPO_LABEL[u.tipo]}{u.modelo ? ` · ${catalogos.modeloLabel[u.modelo] || u.modelo}` : ' · sin modelo'}{u.alias ? ` · ${u.alias}` : ''}
                    </p>
                  </td>
                  <td className="px-6 py-3">
                    <EstadoBadge estado={u.estado} />
                    {u.estado === 'stock' && (
                      <span className="block text-[11px] text-sereno-dark/40 mt-0.5">
                        {u.sinEstrenar ? 'sin estrenar' : 'ya usado'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-sm">{textoDonde({ estado: u.estado, instalaciones: [], ubicacion: u.ubicacion, reparacion: u.reparacion })}</td>
                  <td className="px-6 py-3 text-xs text-sereno-dark/50">{fechaCorta(u.estadoDesde)}</td>
                  <td className="px-6 py-3 text-sm">
                    {u.incidencias === 0 ? (
                      <span className="text-sereno-dark/30">—</span>
                    ) : (
                      <span className={u.incidencias90d > 0 ? 'text-red-700 font-medium' : 'text-sereno-dark/60'} title={u.ultimaIncidencia ? `Última: ${catalogos.incidenciaLabel[u.ultimaIncidencia.tipo] || u.ultimaIncidencia.tipo}, ${fechaCorta(u.ultimaIncidencia.fecha)}` : undefined}>
                        {u.incidencias}{u.incidencias90d > 0 ? ` · ${u.incidencias90d} en 90 d` : ''}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">{children}</th>;
}
